import {
  Button,
  Form,
  Input,
  Select,
  Switch,
  Divider,
  Row,
  Col,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import LocationPicker from "Components/LocationPicker/LocationPicker";
import MediaUpload from "Components/MediaUpload/MediaUpload";
import ItemListSelector from "./_blocks/ItemListSelector";
import {
  emailValidator,
  getColSpan,
  textInputValidator,
  phoneNumberValidator,
  websiteValidator,
} from "commons/helper";

const ds = { fontSize: 12, color: "var(--color-text-tertiary)" };

/**
 * Shared form fields for community create and edit.
 * mode="create" hides the is_active toggle (default).
 * mode="edit"   shows the is_active toggle.
 */
function CommunityForm({
  form,
  locations,
  setLocations,
  masterData,
  mode = "create",
  showTC = false,
  setShowTC,
}) {
  const isEdit = mode === "edit";
  return (
    <Form form={form} layout="vertical" style={{ overflow: "hidden" }}>
      <Divider titlePlacement="left" plain style={ds}>
        Media
      </Divider>
      <Row gutter={16} justify="space-evenly">
        <Col {...getColSpan(isEdit, 2)}>
          <Form.Item
            name="logo_url"
            label="Logo (1:1, min 400×400px, Max 2MB)"
            colon={false}
            rules={[
              {
                required: true,
                message: "Logo is required.",
              },
            ]}
          >
            <MediaUpload
              listType="picture-card"
              maxSizeMB={2}
              aspectRatio={{ width: 1, height: 1, enforce: true }}
              minDimensions={{ width: 400, height: 400 }}
            />
          </Form.Item>
        </Col>
        <Col {...getColSpan(isEdit, 2)}>
          <Form.Item
            name="cover_url"
            label="Cover image (Max 5MB, 9:16 ratio)"
            colon={false}
            rules={[
              {
                required: true,
                message: "Cover image is required.",
              },
            ]}
          >
            <MediaUpload
              listType="picture-card"
              maxSizeMB={5}
              aspectRatio={{ width: 9, height: 16, enforce: true }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider titlePlacement="left" plain style={ds}>
        Basic info
      </Divider>
      <Row gutter={12}>
        <Col sm={12} xs={24}>
          <Form.Item
            name="name"
            label="Community name"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Community name is required.",
              },
              { validator: textInputValidator },
            ]}
          >
            <Input
              placeholder="e.g. Indiranagar Run Club"
              maxLength={100}
              showCount
            />
          </Form.Item>
        </Col>
        <Col sm={12} xs={24}>
          <Form.Item
            name="display_name"
            label="Display name"
            rules={[{ validator: textInputValidator }]}
          >
            <Input placeholder="Display name" maxLength={100} showCount />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="description"
        label="Description"
        rules={[{ validator: textInputValidator }]}
      >
        <Input.TextArea
          placeholder="Tell people what this community is about…"
          rows={5}
          maxLength={500}
          showCount
        />
      </Form.Item>
      {isEdit && masterData?.verification_status === "VERIFIED" && (
        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      )}
      <Form.Item
        name="collection_tags"
        label="Tags (optional, max 5)"
        rules={[
          {
            validator(_, value) {
              if ((value ?? []).length > 5)
                return Promise.reject(new Error("Maximum 10 tags allowed."));
              if ((value ?? []).some((t) => t.length > 30))
                return Promise.reject(
                  new Error("Each tag must be 30 characters or fewer.")
                );
              return Promise.resolve();
            },
          },
        ]}
      >
        <Select
          mode="tags"
          placeholder="e.g. running, fitness, outdoors"
          tokenSeparators={[","]}
          maxTagCount={5}
          options={[]}
        />
      </Form.Item>

      <Divider titlePlacement="left" plain style={ds}>
        Operational locations <span style={{ color: "red" }}>*</span>
      </Divider>
      {locations.map((loc, index) => (
        <LocationPicker
          key={index}
          index={index}
          value={loc}
          cityOptions={masterData?.cityOptions}
          getAreaOptions={masterData?.getAreaOptions}
          loading={masterData?.loadingGeo}
          onChange={(updated) =>
            setLocations((prev) =>
              prev.map((l, i) => (i === index ? updated : l))
            )
          }
          onRemove={
            locations.length > 1
              ? () => setLocations((prev) => prev.filter((_, i) => i !== index))
              : undefined
          }
        />
      ))}
      <Button
        type="dashed"
        block
        onClick={() =>
          setLocations((prev) => [...prev, { city_code: "", area_codes: [] }])
        }
        style={{ marginBottom: 16 }}
      >
        + Add another location
      </Button>

      <Divider titlePlacement="left" plain style={ds}>
        Contact details
      </Divider>
      <Row gutter={12}>
        <Col {...getColSpan(isEdit, 3)}>
          {" "}
          <Form.Item
            name="phone_numbers"
            label="Phone numbers (max 5)"
            rules={[
              { required: true, message: "" },
              {
                validator(_, value) {
                  if (!value?.length)
                    return Promise.reject(
                      new Error("At least one phone number is required.")
                    );
                  if ((value ?? []).length > 5)
                    return Promise.reject(
                      new Error("Maximum 5 phone numbers allowed.")
                    );
                  const invalid = phoneNumberValidator(value);
                  return invalid.length > 0
                    ? Promise.reject(
                        new Error(`Invalid number(s): ${invalid.join(", ")}`)
                      )
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Select
              mode="tags"
              maxTagCount={5}
              placeholder="e.g. 9876543210"
              tokenSeparators={[","]}
              open={false}
              options={[]}
            />
          </Form.Item>
        </Col>
        <Col {...getColSpan(isEdit, 3)}>
          <Form.Item
            name="contact_emails"
            label="Email addresses ( max 5)"
            rules={[
              {
                validator(_, value) {
                  if (!value?.length) return Promise.resolve();
                  if ((value ?? []).length > 5)
                    return Promise.reject(
                      new Error("Maximum 5 email addresses allowed.")
                    );
                  const invalid = emailValidator(value);
                  return invalid.length > 0
                    ? Promise.reject(
                        new Error(`Invalid email(s): ${invalid.join(", ")}`)
                      )
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Select
              mode="tags"
              placeholder="e.g. hello@runclub.com"
              tokenSeparators={[","]}
              open={false}
              maxTagCount={5}
              options={[]}
            />
          </Form.Item>
        </Col>
        <Col {...getColSpan(isEdit, 3)}>
          <Form.Item
            name="contact_websites"
            label="Websites ( max 5)"
            rules={[
              {
                validator(_, value) {
                  if (!value?.length) return Promise.resolve();
                  if ((value ?? []).length > 5)
                    return Promise.reject(
                      new Error("Maximum 5 websites allowed.")
                    );
                  const invalid = websiteValidator(value);
                  return invalid.length > 0
                    ? Promise.reject(
                        new Error(`Invalid URL(s): ${invalid.join(", ")}`)
                      )
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Select
              mode="tags"
              placeholder="e.g. https://runclub.in"
              tokenSeparators={[","]}
              open={false}
              maxTagCount={5}
              options={[]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider titlePlacement="left" plain style={ds}>
        Community Lead Name <span style={{ color: "red" }}>*</span>
      </Divider>
      <Form.List name="owner_metadata">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, ...field }, index) => (
              <div
                key={key}
                style={{
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  padding: "12px 12px 4px",
                  marginBottom: 12,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(field.name)}
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 0,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 22,
                      lineHeight: 1,
                      color: "var(--color-text-tertiary)",
                    }}
                  >
                    ×
                  </button>
                )}
                <Row gutter={16}>
                  <Col {...getColSpan(isEdit, 3)}>
                    <Form.Item
                      key={key}
                      {...field}
                      name={[field.name, "name"]}
                      label={`Community Lead Name ${index + 1}`}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Community Lead Name is required.",
                        },
                        { validator: textInputValidator },
                      ]}
                    >
                      <Input placeholder="e.g. Rahul Sharma" maxLength={50} />
                    </Form.Item>
                  </Col>
                  <Col {...getColSpan(isEdit, 3)}>
                    <Form.Item
                      {...field}
                      name={[field.name, "phone_numbers"]}
                      label="Phone (optional)"
                      rules={[
                        {
                          whitespace: true,
                          validator(_, value) {
                            if (
                              value === null ||
                              value === undefined ||
                              String(value).length === 0
                            ) {
                              return Promise.resolve();
                            }
                            const invalid = phoneNumberValidator(value);
                            return invalid.length > 0
                              ? Promise.reject(new Error(`Invalid number`))
                              : Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder="e.g. 9876543210"
                        maxLength={10}
                        inputMode="numeric"
                      />
                    </Form.Item>
                  </Col>
                  <Col {...getColSpan(isEdit, 3)}>
                    <Form.Item
                      {...field}
                      name={[field.name, "hosting_since"]}
                      label="Hosting Since"
                      rules={[
                        {
                          required: true,
                          message: "Hosting since is required.",
                        },
                        {
                          validator(_, v) {
                            if (!v) return Promise.resolve();
                            const selected = dayjs.isDayjs(v) ? v : dayjs(v);
                            if (
                              selected.isValid() &&
                              selected.isAfter(dayjs().endOf("day"))
                            )
                              return Promise.reject(
                                new Error(
                                  "Hosting since cannot be a future date."
                                )
                              );
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <DatePicker
                        placeholder="2020-01-15"
                        style={{ minWidth: "100%" }}
                        disabledDate={(d, info) => {
                          if (info?.type === "date")
                            return d && d.isAfter(dayjs(), "day");
                          return false;
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col {...getColSpan(isEdit, 2)}>
                    <Form.Item
                      {...field}
                      name={[field.name, "description"]}
                      label="Description (optional)"
                      rules={[{ validator: textInputValidator }]}
                    >
                      <Input.TextArea
                        placeholder="Short bio…"
                        rows={6}
                        maxLength={225}
                        showCount
                      />
                    </Form.Item>
                  </Col>
                  <Col {...getColSpan(isEdit, 2)}>
                    <Form.Item
                      {...field}
                      name={[field.name, "logo_image_url"]}
                      label="Logo (1:1, min 400×400px, Max 2MB)"
                      rules={[
                        {
                          required: true,
                          message: "Logo is required.",
                        },
                      ]}
                    >
                      <MediaUpload
                        listType="picture-card"
                        maxSizeMB={2}
                        aspectRatio={{ width: 1, height: 1, enforce: true }}
                        minDimensions={{ width: 400, height: 400 }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            ))}
            {fields.length < 5 && (
              <Button
                type="dashed"
                block
                onClick={() => add({ name: "" })}
                style={{ marginBottom: 16 }}
              >
                + Add another Community Lead Name
              </Button>
            )}
          </>
        )}
      </Form.List>

      <Row gutter={12}>
        <Col {...getColSpan(isEdit, 2)}>
          <Divider titlePlacement="left" plain style={ds}>
            What we do (optional)
          </Divider>
          <Form.Item
            name="what_we_do"
            label="What we do"
            rules={[
              {
                validator(_, val) {
                  const missing = (val ?? []).find(
                    (v) => !v.description?.trim()
                  );
                  return missing
                    ? Promise.reject(
                        new Error(`Add a description for "${missing.name}".`)
                      )
                    : Promise.resolve();
                },
              },
            ]}
          >
            <ItemListSelector
              masterItems={masterData?.whatWeDo ?? []}
              nameKey="what_we_do_name"
              loading={masterData?.loading}
              placeholder="Add a category…"
            />
          </Form.Item>
        </Col>
        <Col {...getColSpan(isEdit, 2)}>
          {" "}
          <Divider titlePlacement="left" plain style={ds}>
            Activities
          </Divider>
          <Form.Item name="activities" label="Activities" rules={[{ required: true, message: "Activities are required." }]}>
            <ItemListSelector
              masterItems={masterData?.activities ?? []}
              nameKey="activity_name"
              loading={masterData?.loading}
              placeholder="Add an activity…"
              showDescription={false}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider titlePlacement="left" plain style={ds}>
        Terms &amp; Conditions (optional)
      </Divider>

      {/* Switch row */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 13 }}>Add Terms &amp; Conditions</span>
        <Switch
          checked={showTC}
          size="small"
          onChange={(checked) => {
            setShowTC(checked);
            if (checked) {
              const existing = form.getFieldValue("tc_sections") ?? [];
              if (existing.length === 0) {
                form.setFieldValue("tc_sections", [
                  { heading: "", content: "" },
                ]);
              }
            }
          }}
        />
      </Row>

      {showTC && (
        <>
          <Row gutter={12}>
            <Col {...getColSpan(isEdit, 2)}>
              {" "}
              <Form.Item
                name="tc_title"
                label="Title"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "T&C title is required.",
                  },
                  { validator: textInputValidator },
                ]}
              >
                <Input
                  placeholder="Terms & Conditions"
                  maxLength={100}
                  showCount
                />
              </Form.Item>
            </Col>
            <Col {...getColSpan(isEdit, 2)}>
              <Form.Item
                name="tc_sub_title"
                label="Sub-title"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "T&C sub-title is required.",
                  },
                  { validator: textInputValidator },
                ]}
              >
                <Input
                  placeholder="Please read carefully…"
                  maxLength={100}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.List name="tc_sections">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, ...field }) => (
                  <div
                    key={key}
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: 8,
                      padding: "12px 12px 4px",
                      marginBottom: 8,
                      position: "relative",
                    }}
                  >
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => remove(field.name)}
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 18,
                          lineHeight: 1,
                          color: "var(--color-text-tertiary)",
                        }}
                      >
                        ×
                      </button>
                    )}
                    <Form.Item
                      key={key}
                      {...field}
                      name={[field.name, "heading"]}
                      label="Section heading"
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Section heading is required.",
                        },
                        { validator: textInputValidator },
                      ]}
                    >
                      <Input
                        placeholder="e.g. Refund Policy"
                        maxLength={100}
                        showCount
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, "content"]}
                      label="Content"
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Section content is required.",
                        },
                        { validator: textInputValidator },
                      ]}
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder="Section content…"
                        maxLength={500}
                        showCount
                      />
                    </Form.Item>
                  </div>
                ))}
                <Button
                  type="dashed"
                  block
                  onClick={() => add({ heading: "", content: "" })}
                  style={{ marginBottom: 16 }}
                >
                  + Add T&amp;C section
                </Button>
              </>
            )}
          </Form.List>
        </>
      )}
    </Form>
  );
}

export default CommunityForm;
